import { ApiClient } from './client';
import type {
  Story,
  EnglishLevel,
  UserAnswer,
  StoryResult,
} from '@/types/story.types';

export interface StoryAssignmentParams {
  userLevel: EnglishLevel;
  count?: number;
  excludeCompleted?: boolean;
  category?: string;
}

function mapResource(r: Record<string, unknown>): Record<string, unknown> {
  if (r.type === 'audio') return { ...r, audioUrl: r.audioUrl ?? r.url };
  if (r.type === 'video') return { ...r, videoUrl: r.videoUrl ?? r.url };
  return r;
}

function mapApiStoryToStory(raw: Record<string, unknown>): Story {
  const rawResources = Array.isArray(raw.resources) ? raw.resources : [];
  return {
    ...raw,
    resources: rawResources.map((r) => mapResource(r as Record<string, unknown>)),
  } as unknown as Story;
}

export async function getRecommendedStories(
  params: StoryAssignmentParams
): Promise<Story[]> {
  const { userLevel, count = 3, category } = params;

  const queryParams = new URLSearchParams({
    levels: userLevel,
    limit: count.toString(),
  });
  if (category) queryParams.append('category', category);

  const raw = await ApiClient.get<unknown[]>(`/stories/recommended?${queryParams.toString()}`);
  return raw.map((r) => mapApiStoryToStory(r as Record<string, unknown>));
}

export async function getStoryById(storyId: string): Promise<Story> {
  const raw = await ApiClient.get<Record<string, unknown>>(`/stories/${storyId}`);
  return mapApiStoryToStory(raw);
}

export async function updateStoryTime(storyId: string, timeSpent: number): Promise<void> {
  await ApiClient.patch(`/stories/${storyId}/progress`, { timeSpent });
}

export interface StoryUserProgress {
  storyId: string;
  timeSpent: number;
  /** Primera escucha completa del audio (ms); null = nunca terminado. */
  audioListenedAt: number | null;
}

/** Progreso per-user de la historia (endpoint autenticado, sin caché edge). */
export async function getStoryProgress(storyId: string): Promise<StoryUserProgress> {
  return ApiClient.get<StoryUserProgress>(`/stories/${storyId}/progress`);
}

/**
 * Marca el audio de la historia como escuchado por completo. Idempotente en el
 * backend (conserva la primera escucha), así que puede llamarse en cada fin de
 * reproducción sin más control.
 */
export async function markStoryAudioListened(storyId: string): Promise<void> {
  await ApiClient.post(`/stories/${storyId}/audio-listened`, {});
}

/**
 * Asignación inicial idempotente: si el alumno ya tiene nivel y aún no tiene
 * ninguna historia asignada, el backend le siembra su primer lote (meta +
 * backlog). Se llama al completar el onboarding de personalización para que vea
 * contenido sin esperar al cron semanal.
 */
export async function assignInitialStories(): Promise<{ assigned: number }> {
  return ApiClient.post<{ assigned: number }>('/stories/assign-initial', {});
}

export async function submitStory(
  storyId: string,
  data: { answers: UserAnswer[]; timeSpent: number }
): Promise<StoryResult> {
  const raw = await ApiClient.post<Record<string, unknown>>(
    `/stories/${storyId}/submit`,
    { answers: data.answers, timeSpent: data.timeSpent }
  );

  return {
    storyId: raw.storyId as string,
    userId: raw.userId as string,
    score: raw.score as number,
    totalPoints: raw.totalPoints as number,
    earnedPoints: raw.earnedPoints as number,
    xpEarned: raw.xpEarned as number,
    timeSpent: raw.timeSpent as number,
    activityResults: (raw.activityResults ?? []) as StoryResult['activityResults'],
    completedAt: raw.completedAt as string,
    passed: raw.passed as boolean,
  };
}

export async function getAllStories(params?: {
  level?: EnglishLevel;
  category?: string;
  search?: string;
}): Promise<Story[]> {
  const queryParams = new URLSearchParams();
  if (params?.level) queryParams.append('level', params.level);
  if (params?.category) queryParams.append('category', params.category);
  if (params?.search) queryParams.append('search', params.search);

  const qs = queryParams.toString();
  const raw = await ApiClient.get<unknown[]>(`/stories${qs ? `?${qs}` : ''}`);
  return raw.map((r) => mapApiStoryToStory(r as Record<string, unknown>));
}
